import React, { useCallback } from 'react'
import { BrowserView } from 'react-device-detect'
import { ProductImage } from 'app/common/Common'
import jsQR from 'jsqr'
import _ from 'lodash'
import {
  $DCprimaryColor,
  DcButton,
  DcFlexBox,
  DcInput,
  PixelCurrency,
  PixelFactoryContext,
  PixelText,
} from 'pixel-base'
import styled from 'styled-components'
import { get, post } from 'utils/api-routes.util'

import OrderScannerModal from './OrderScannerModal'
import ProductSelectionModal from './ProductSelectionModal'
import ProductLineItem from './subComponents/ProductLineItem'
import { IPixelFactory } from '../../../../../decl.d'
import { ScanBarcodeIcon } from '../../../../utils/icons/scan-barcode-icon'
interface BasicAddress {
  phone: string
  email: string
  address1?: string
}

interface NewOrderDetails {
  billing: Partial<BasicAddress>
  shipping: Partial<BasicAddress>
  email: string
  customer_id: number
}

interface AddressFull {
  first_name: string
  last_name: string
  customer_reference: string
  phone: string
  email: string
  address1: string
  city: string
  country: string
  country_code: string
  state: string
  state_code: string
  postcode: string
}

interface Meta {
  added_by: string
  sale_by_id: string
  sale_by: string
  order_source: string
}

interface Courier {
  courier_name: string
  tracking_id: string
}

interface LineItem {
  order_id: number
  quantity: number
  product_id: number
  variation_id: number
  price: number
  total: number
  sku: string
  image: string | null
  weight: number | null
  description: string | null
  line_item_id: number
  name: string
  is_partial_return: boolean | null
  has_properties: boolean | null
  fulfillment_order_id: number | null
  fulfillment_order_line_item_id: number | null
}

interface OrderHistory {
  order_id: number
  updated_value: string
  change_type: string
  user_name: string
  update_time: string
  is_bulk: boolean
  message: string
}

interface OrderDetails {
  is_billing_address_same: boolean
  customer_id: number
  is_editable: boolean
  shipping: AddressFull
  billing: AddressFull
  total: number
  weight: number
  order_discount: number
  complaint_count: number
  source_order_id: number
  split_source_order_id: string
  store_order_id: number
  store_id: number
  store_name: string
  status: string
  tags: string
  number: number
  created_via: string
  last_comment: string
  date_created: string
  is_refuser: boolean
  is_returning: boolean
  is_duplicate: boolean
  dispatch_id: number
  is_split_child: boolean
  is_split_main: boolean
  split_child_ids: string
  split_main_id: string
  exchanged_from_order_id: string
  print_customer_note_on_shipping_label: boolean
  print_customer_note_on_invoice: boolean
  meta: Meta
  courier: Courier
  payments: any[]
  order_notes: any[]
  order_tags: any[]
  courier_transactions: any[]
  line_items: LineItem[]
  order_history: OrderHistory[]
  discount_lines: any[]
  order_shipping: any[]
  order_tax: any[]
  daraz_payment_transactions: any[]
}

interface IProps {
  detailID: number
  details: OrderDetails
  ComplaintSection: boolean
  newOrder: boolean
  newOrderData: NewOrderDetails
  setDetails: (details: OrderDetails) => void
  data: Record<string, any>
  setData: (data: Record<string, any>) => void
  setNewOrderData: (newOrderData: NewOrderDetails) => void
}

const OrderProduct = (props: IProps) => {
  const {
    details,
    detailID,
    setDetails,
    data,
    setData,
    ComplaintSection,
    newOrder,
    setNewOrderData,
    newOrderData,
  } = props

  const { showPageLoader, hidePageLoader, showFailure } =
    React.useContext<IPixelFactory>(PixelFactoryContext)
  const [customerLoading, setCustomerLoading] = React.useState(false)
  const [products, setProducts] = React.useState([])

  // Scanner states
  const [showScanner, setShowScanner] = React.useState(false)
  const [scannedSKU, setScannedSKU] = React.useState('')
  const [showProductSelection, setShowProductSelection] = React.useState(false)
  const [scannedProducts, setScannedProducts] = React.useState([])
  const scannerRef = React.useRef<HTMLVideoElement>(null)
  const handleAddProduct = (detailID, newProduct) => {
    if (ComplaintSection === true) {
      setData({
        ...data,
        exchange_products: [...data.exchange_products, newProduct],
      })
    } else {
      showPageLoader()
      post(`/orders/${detailID}/products`, newProduct).subscribe((res: any) => {
        hidePageLoader()
        if (res.response.data) {
          setDetails(res.response.data)
        } else {
          showFailure(res.response.error)
        }
      })
    }
  }
  const handleSearch = (query, isFromScan = false) => {
    setCustomerLoading(true)
    get(`/products/lineitems?search=${query}`).subscribe((res: any) => {
      setProducts(res.response)
      setCustomerLoading(false)

      // If this was triggered by a scan, handle the results
      if (isFromScan && res.response) {
        handleScannedProductsFound(res.response)
      }
    })
  }

  const debounceFn = useCallback(_.debounce(handleSearch, 1000), [])

  // Scanner functions
  const startScanner = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
      })
      if (scannerRef.current) {
        scannerRef.current.srcObject = stream
        scannerRef.current.onloadedmetadata = () => {
          startScanning()
        }
      }
    } catch (err) {
      showFailure('Unable to access camera')
      console.error('Camera access error:', err)
    }
  }

  const startScanning = () => {
    if (!scannerRef.current) return

    const canvas = document.createElement('canvas')
    const context = canvas.getContext('2d')
    if (!context) return

    const scanFrame = () => {
      if (!scannerRef.current || !context) return

      const video = scannerRef.current
      const { videoWidth, videoHeight } = video

      if (videoWidth === 0 || videoHeight === 0) {
        requestAnimationFrame(scanFrame)
        return
      }

      canvas.width = videoWidth
      canvas.height = videoHeight

      // Draw the video frame to canvas
      context.drawImage(video, 0, 0, videoWidth, videoHeight)

      // Get image data for scanning
      const imageData = context.getImageData(0, 0, videoWidth, videoHeight)

      // Scan for QR codes and barcodes
      const code = jsQR(imageData.data, imageData.width, imageData.height, {
        inversionAttempts: 'dontInvert',
      })

      if (code) {
        // Found a code, process it
        setScannedSKU(code.data)
        setShowScanner(false)
        stopScanner()

        if (code.data) {
          handleScan(code.data)
        }
        return
      }

      // Continue scanning
      requestAnimationFrame(scanFrame)
    }

    scanFrame()
  }

  const stopScanner = () => {
    if (scannerRef.current && scannerRef.current.srcObject) {
      const stream = scannerRef.current.srcObject as MediaStream
      stream.getTracks().forEach((track) => track.stop())
    }
  }

  const handleScan = (scannedData: string) => {
    if (!scannedData) return

    setScannedSKU(scannedData)
    setShowScanner(false)
    stopScanner()

    // Search for products with the scanned SKU
    handleSearch(scannedData, true)
  }

  const handleScannedProductsFound = (foundProducts: any[]) => {
    if (foundProducts.length === 0) {
      showFailure(`No products found with SKU: ${scannedSKU}`)
      return
    }

    if (foundProducts.length === 1) {
      // Single product found, add it directly
      const product = foundProducts[0]
      const obj = {
        image: product.image,
        name: product.product_name,
        price: product.price,
        quantity: 1,
        product_id: product.product_id,
        sku: product.sku,
        tax: product.tax || 0,
        variation_id: product.variation_id,
        weight: product.weight,
      }

      if (ComplaintSection) {
        handleAddProduct(detailID, obj)
      } else if (newOrder) {
        // Check if product already exists
        if (
          newOrderData?.line_items?.find(
            (item) =>
              item.product_id === obj.product_id &&
              item.variation_id === obj.variation_id,
          )
        ) {
          showFailure('Product already added')
        } else {
          setNewOrderData({
            ...newOrderData,
            line_items: [...(newOrderData?.line_items || []), obj],
          })
        }
      } else {
        handleAddProduct(detailID, obj)
      }
    } else {
      // Multiple products found, show selection modal
      setScannedProducts(foundProducts)
      setShowProductSelection(true)
    }
  }

  const handleAddSelectedProducts = (selectedProducts: any[]) => {
    if (ComplaintSection) {
      // For complaint section, add each product individually
      selectedProducts.forEach((product) => {
        const obj = {
          image: product.image,
          name: product.product_name,
          price: product.price,
          quantity: 1,
          product_id: product.product_id,
          sku: product.sku,
          tax: product.tax || 0,
          variation_id: product.variation_id,
          weight: product.weight,
        }
        handleAddProduct(detailID, obj)
      })
    } else if (newOrder) {
      // For new order, collect all products and update state once
      const newProducts = selectedProducts.map((product) => ({
        image: product.image,
        name: product.product_name,
        price: product.price,
        quantity: 1,
        product_id: product.product_id,
        sku: product.sku,
        tax: product.tax || 0,
        variation_id: product.variation_id,
        weight: product.weight,
      }))

      // Filter out duplicates
      const existingProducts = newOrderData?.line_items || []
      const uniqueNewProducts = newProducts.filter(
        (newProduct) =>
          !existingProducts.find(
            (existing) =>
              existing.product_id === newProduct.product_id &&
              existing.variation_id === newProduct.variation_id,
          ),
      )

      if (uniqueNewProducts.length > 0) {
        setNewOrderData({
          ...newOrderData,
          line_items: [...existingProducts, ...uniqueNewProducts],
        })
      }
    } else {
      // For existing order, add each product individually
      selectedProducts.forEach((product) => {
        const obj = {
          image: product.image,
          name: product.product_name,
          price: product.price,
          quantity: 1,
          product_id: product.product_id,
          sku: product.sku,
          tax: product.tax || 0,
          variation_id: product.variation_id,
          weight: product.weight,
        }
        handleAddProduct(detailID, obj)
      })
    }
  }

  const display = () => {
    const de = ComplaintSection
      ? data?.exchange_products?.length === 0
      : newOrder
      ? newOrderData?.line_items?.length === 0
      : details?.line_items?.length === 0
    return de
  }
  return (
    <Container>
      <InnerSection>
        <SectionHeader>
          <DcFlexBox
            style={{
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
            }}
          >
            <PixelText
              textSize="16px"
              style={{
                fontWeight: '600',
                padding: '10px',
                color: 'rgba(0, 0, 0, 0.84)',
              }}
            >
              {' '}
              {newOrder ? (
                'Add Product'
              ) : (
                <> {ComplaintSection === true ? 'Add products' : 'Products'}</>
              )}
            </PixelText>
          </DcFlexBox>
        </SectionHeader>
        {ComplaintSection === true ? (
          <InnerSectionContent>
            <FormGroup>
              <DcInput
                required
                invalid={true}
                type={'text'}
                name={'name'}
                as={'typeahead'}
                labelKey={'product_name'}
                data={products}
                isClearOnSelection={true}
                placeholder={'Search product'}
                handleSearch={debounceFn}
                isLoading={customerLoading}
                isShowIcon={true}
                icon={<ScanBarcodeIcon size={16} />}
                iconPosition="right"
                iconClick={() => {
                  setShowScanner(true)
                  setScannedSKU('')
                  setTimeout(() => startScanner(), 100)
                }}
                formatter={(option) => {
                  return (
                    <ProductContainer justify padding>
                      <Product>
                        <ImageSection>
                          <ProductImage
                            style={{
                              width: '38px',
                              height: '38px',
                              marginRight: '5px',
                              marginTop: '5px',
                              borderRadius: '3px',
                              border: '1px solid #e6e6e6',
                            }}
                            path={option.image}
                          />
                        </ImageSection>
                        <ProductSection>
                          <Address color="primary">
                            {option.product_name}
                          </Address>
                          <Address>sku: {option.sku}</Address>
                          <Address>
                            Price: <PixelCurrency value={option.price} /> &nbsp;
                            &nbsp; Stock: {option.stock_quantity}
                          </Address>
                        </ProductSection>
                      </Product>
                    </ProductContainer>
                  )
                }}
                onChange={(array) => {
                  if (array.length === 0) {
                    return
                  }
                  const obj = {
                    image: array[0].image,
                    name: array[0].product_name,
                    price: array[0].price,
                    quantity: 1,
                    product_id: array[0].product_id,
                    sku: array[0].sku,
                    tax: array[0].tax || 0,
                    variation_id: array[0].variation_id,
                    weight: array[0].weight,
                  }

                  handleAddProduct(detailID, obj)
                  debounceFn()
                }}
              />
            </FormGroup>
          </InnerSectionContent>
        ) : (
          <>
            {newOrder ? (
              // ---------------------- NEW ORDER ----------------------
              <InnerSectionContent>
                <FormGroup>
                  <DcInput
                    required
                    type={'text'}
                    name={'name'}
                    as={'typeahead'}
                    labelKey={'product_name'}
                    isClearOnSelection={true}
                    data={products}
                    placeholder={'Search product'}
                    handleSearch={debounceFn}
                    isLoading={customerLoading}
                    isShowIcon={true}
                    icon={<ScanBarcodeIcon size={16} />}
                    iconPosition="right"
                    iconClick={() => {
                      setShowScanner(true)
                      setScannedSKU('')
                      setTimeout(() => startScanner(), 100)
                    }}
                    formatter={(option) => {
                      return (
                        <ProductContainer justify>
                          <Product>
                            <ImageSection>
                              <ProductImage
                                style={{
                                  width: '38px',
                                  height: '38px',
                                  marginRight: '5px',
                                  marginTop: '5px',
                                  borderRadius: '3px',
                                  border: '1px solid #e6e6e6',
                                }}
                                path={option.image}
                              />
                            </ImageSection>
                            <ProductSection>
                              <Address color="primary">
                                {option.product_name}
                              </Address>
                              <Address>sku: {option.sku}</Address>

                              <Address>
                                Price: <PixelCurrency value={option.price} />
                                &nbsp; &nbsp; Stock: {option.stock_quantity}
                              </Address>
                            </ProductSection>
                          </Product>
                        </ProductContainer>
                      )
                    }}
                    onChange={(array) => {
                      if (array.length === 0) {
                        return
                      }
                      const obj = {
                        line_item_id: null,
                        image: array[0].image,
                        name: array[0].product_name,
                        price: array[0].price,
                        quantity: 1,
                        product_id: array[0].product_id,
                        sku: array[0].sku,
                        source_lineitem_id: null,
                        tax: array[0].tax || 0,
                        variation_id: array[0].variation_id,
                        weight: array[0].weight,
                      }
                      debounceFn()
                      // check if the product is already in the cart then dont add it again expect if product_id === 0
                      if (
                        newOrderData?.line_items?.find(
                          (item) =>
                            item.product_id === obj.product_id &&
                            item.variation_id === obj.variation_id,
                        )
                      ) {
                        showFailure('Product already added')
                      } else {
                        setNewOrderData({
                          ...newOrderData,
                          line_items: [
                            ...(newOrderData?.line_items || []),
                            obj,
                          ],
                        })
                      }
                    }}
                  />
                </FormGroup>
              </InnerSectionContent>
            ) : (
              <>
                {details?.is_editable === true && (
                  <InnerSectionContent>
                    <FormGroup>
                      <DcInput
                        required
                        type={'text'}
                        name={'name'}
                        as={'typeahead'}
                        labelKey={'product_name'}
                        data={products}
                        isClearOnSelection={true}
                        placeholder={'Search product'}
                        handleSearch={debounceFn}
                        isLoading={customerLoading}
                        isShowIcon={true}
                        icon={<ScanBarcodeIcon size={16} />}
                        iconPosition="right"
                        iconClick={() => {
                          setShowScanner(true)
                          setScannedSKU('')
                          setTimeout(() => startScanner(), 100)
                        }}
                        formatter={(option) => {
                          return (
                            <ProductContainer justify>
                              <Product>
                                <ImageSection>
                                  <ProductImage
                                    style={{
                                      width: '38px',
                                      height: '38px',
                                      marginRight: '5px',
                                      marginTop: '5px',
                                      borderRadius: '3px',
                                      border: '1px solid #e6e6e6',
                                    }}
                                    path={option.image}
                                  />
                                </ImageSection>
                                <ProductSection>
                                  <Address color="primary">
                                    {option.product_name}
                                  </Address>
                                  <Address>sku: {option.sku}</Address>

                                  <Address>
                                    Price:
                                    <PixelCurrency value={option.price} />
                                    &nbsp; &nbsp; Stock: {option.stock_quantity}
                                  </Address>
                                </ProductSection>
                              </Product>
                            </ProductContainer>
                          )
                        }}
                        onChange={(array) => {
                          if (array.length === 0) {
                            return
                          }
                          const obj = {
                            image: array[0]?.image,
                            name: array[0]?.product_name,
                            price: array[0]?.price,
                            quantity: 1,
                            product_id: array[0]?.product_id,
                            sku: array[0]?.sku,
                            tax: array[0]?.tax || 0,
                            variation_id: array[0]?.variation_id,
                            weight: array[0]?.weight,
                          }

                          handleAddProduct(detailID, obj)
                          debounceFn()
                        }}
                      />
                    </FormGroup>
                  </InnerSectionContent>
                )}
              </>
            )}
          </>
        )}
      </InnerSection>
      <BrowserView>
        <InnerSection className={display() ? 'display' : ''} header>
          <ProductContainer padding>
            <Product style={{ fontSize: '12px' }}>
              {newOrder ? (
                'Products'
              ) : (
                <>
                  Products
                  {!ComplaintSection && (
                    <>
                      {details?.line_items?.length === 0 ? (
                        ''
                      ) : (
                        <>({details?.line_items?.length})</>
                      )}
                    </>
                  )}
                </>
              )}
            </Product>
            <Quantity style={{ fontSize: '13px' }}>Qty</Quantity>
            <Price style={{ fontSize: '13px' }}>Price</Price>
            <Total style={{ fontSize: '13px' }} justify>
              Total
            </Total>
            {ComplaintSection ? (
              <Actions style={{ fontSize: '13px' }}>Actions</Actions>
            ) : (
              <>
                {newOrder ? (
                  <Actions style={{ fontSize: '13px' }}>Actions</Actions>
                ) : (
                  <>
                    {details?.is_editable === true && (
                      <Actions style={{ fontSize: '13px' }}>Actions</Actions>
                    )}
                  </>
                )}
              </>
            )}
          </ProductContainer>
        </InnerSection>
      </BrowserView>

      {ComplaintSection === true ? (
        <>
          {data?.exchange_products?.map((item: any, index: number) => (
            <ProductLineItem
              key={index}
              item={item}
              setData={setData}
              detailID={detailID}
              data={data}
              complaintSection={ComplaintSection}
            />
          ))}
        </>
      ) : (
        <>
          {newOrder ? (
            <>
              {newOrderData?.line_items?.map((key, index: number) => (
                /// ------------------- new order --------------------------
                <ProductLineItem
                  key={index}
                  item={key}
                  setDetails={setDetails}
                  detailID={detailID}
                  details={details}
                  complaintSection={ComplaintSection}
                  newOrder={newOrder}
                  newOrderData={newOrderData}
                  setNewOrderData={setNewOrderData}
                />
              ))}
            </>
          ) : (
            <>
              {details?.line_items?.map((key, index: number) => (
                <ProductLineItem
                  key={index}
                  item={key}
                  setDetails={setDetails}
                  detailID={detailID}
                  details={details}
                  complaintSection={ComplaintSection}
                />
              ))}
            </>
          )}
        </>
      )}

      {/* Scanner Modal */}
      <OrderScannerModal
        isOpen={showScanner}
        onClose={() => {
          setShowScanner(false)
          stopScanner()
        }}
        scannedSKU={scannedSKU}
        setScannedSKU={setScannedSKU}
        handleScan={handleScan}
        scannerRef={scannerRef}
      />

      {/* Product Selection Modal */}
      <ProductSelectionModal
        isOpen={showProductSelection}
        onClose={() => {
          setShowProductSelection(false)
          setScannedProducts([])
        }}
        products={scannedProducts}
        scannedSKU={scannedSKU}
        onAddSelected={handleAddSelectedProducts}
      />
    </Container>
  )
}
const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`
const InnerSection = styled.div<{ header?: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-content: center;
  border-bottom: 1px solid #e6e6e6;
  width: 100%;
  padding: 10px 0px;
  background: ${(props) => (props.header ? '#0168f912' : 'none')};
  &.display {
    display: none;
  }
`
const SectionHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  /* margin: 0px 10px; */
`

const InnerSectionContent = styled.div<{ direction?: boolean; gap?: string }>`
  display: flex;
  flex-direction: ${(props) => (props.direction ? 'row' : 'column')};
  gap: ${(props) => props.gap};
  margin: 10px;
`
const Address = styled.div<{ color?: string }>`
  font-size: 12px;
  font-weight: 400;
  color: ${(props) =>
    props.color === 'primary' ? $DCprimaryColor : 'rgba(0, 0, 0, 0.87)'};
`

const FormGroup = styled.div<{ flexEnd?: boolean }>`
  display: inline-flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: flex-end;
  align-items: flex-end;
  align-content: flex-start;
  width: 100%;
  flex-grow: 1;
  gap: 10px;
  .full-width {
    flex-grow: 1;
  }
`
const ProductContainer = styled.div<{ justify?: boolean; padding?: boolean }>`
  display: flex;
  flex-direction: row;
  justify-content: ${(props) =>
    props.justify ? 'flex-start' : 'space-around'};
  align-items: center;
  align-content: center;
  width: 100%;
  padding: ${(props) => (props.padding ? '0px 10px 0px 20px' : '15px')};
`
const ProductSection = styled.div``
const ImageSection = styled.div``
const Product = styled.div`
  display: flex;
  flex-direction: row;
  width: 55%;
  gap: 10px;
`
const Quantity = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  width: 10%;
`
const Price = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  width: 10%;
`
const Total = styled.div<{ justify?: boolean }>`
  display: flex;
  flex-direction: row;
  justify-content: center;
  width: 15%;
`
const Actions = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-evenly;
  align-items: center;
  width: 15%;
`

export default OrderProduct
